import { Dispatch, FormEvent, SetStateAction, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import type { TFunction } from "i18next"
import { Navigate, useNavigate, useParams } from "react-router-dom"
import { faAt, faUser } from "@fortawesome/free-solid-svg-icons"
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  Check,
  CheckCircle2,
  LayoutList,
  LoaderCircle,
  MessageCircle,
  Package,
  Rocket,
  Shirt,
  ShoppingBag,
  Smartphone,
  Sparkles,
  Store,
  Target,
  Truck,
  WandSparkles,
} from "lucide-react"
import Animate, { AnimationTypes } from "../../components/Animate/Animate"
import InputField from "../../components/InputField/InputField"
import Navbar from "../../components/LandPage/Navbar"
import { SEO } from "../../seo/Seo"
import { PATH_MAP } from "../../configuration/PathMap"
import { trackLeadConversion, trackMarketingEvent } from "../../analytics/tracking"
import {
  PublicAnimatedBackground,
  publicAnimatedBackgroundShellClassName,
} from "../../components/PublicAnimatedBackground/PublicAnimatedBackground"
import {
  defaultMarketingQuizSlug,
  evaluateMarketingQuiz,
  getMarketingQuiz,
  marketingQuizzes,
  type MarketingQuiz,
  type MarketingQuizAnswers,
  type MarketingQuizQuestion,
  type MarketingQuizResult,
} from "../../configuration/MarketingQuizzes"
import { useCreateLandingLead } from "../../hooks/LandingLead/useCreateLandingLead"

type QuestionText = { title: string; subtitle: string; options: Record<string, string> }

type Contact = { name: string; email: string }

type ResultFormProps = {
  t: TFunction<"leadQuiz">
  quiz: MarketingQuiz
  outcomeId: string
  contact: Contact
  setContact: Dispatch<SetStateAction<Contact>>
  feedback: string
  isSubmitting: boolean
  onSubmit: (event: FormEvent) => Promise<void>
  onBack: () => void
}

type CalculatingProps = { t: TFunction<"leadQuiz"> }

type SuccessProps = { t: TFunction<"leadQuiz">; quiz: MarketingQuiz; outcomeId: string; onClick: () => void }

const optionIcons = [
  Store,
  Building2,
  Rocket,
  Package,
  ShoppingBag,
  Smartphone,
  Shirt,
  Package,
  LayoutList,
  WandSparkles,
  MessageCircle,
  Truck,
  Target,
]

const quizStepParam = "etapa"

const calculationDelayMs = 1300

const getQuestionParam = (questionId: string) => `q_${questionId}`

const getQuizPath = (slug: string) => `/quiz/${slug}`

const restoreQuizState = (quiz: MarketingQuiz) => {
  const params = new URLSearchParams(window.location.search)

  const answers = quiz.questions.reduce<MarketingQuizAnswers>((current, question) => {
    const selectedOptions = (params.get(getQuestionParam(question.id)) ?? "")
      .split(",")
      .map((value) => question.options[Number.parseInt(value, 10)]?.id)
      .filter((optionId): optionId is string => Boolean(optionId))

    if (selectedOptions.length > 0) current[question.id] = selectedOptions

    return current
  }, {})

  const requestedStep = Number.parseInt(params.get(quizStepParam) ?? "0", 10)

  const step = Number.isInteger(requestedStep) ? Math.min(Math.max(requestedStep, 0), quiz.questions.length) : 0

  return { answers, step }
}

const persistQuizState = (quiz: MarketingQuiz, answers: MarketingQuizAnswers, step: number) => {
  const params = new URLSearchParams(window.location.search)

  params.set(quizStepParam, String(step))

  quiz.questions.forEach((question) => {
    const optionIndexes = (answers[question.id] ?? [])
      .map((answer) => question.options.findIndex((option) => option.id === answer))
      .filter((index) => index >= 0)

    if (optionIndexes.length > 0) {
      params.set(getQuestionParam(question.id), optionIndexes.join(","))
      return
    }

    params.delete(getQuestionParam(question.id))
  })

  window.history.replaceState(
    window.history.state,
    "",
    `${window.location.pathname}?${params.toString()}${window.location.hash}`,
  )
}

export default function LeadQuizLandingPage() {
  const { quizSlug } = useParams()

  const { t } = useTranslation("leadQuiz")

  const navigate = useNavigate()

  const { handleCreateLandingLead } = useCreateLandingLead()

  const quiz = useMemo(() => getMarketingQuiz(quizSlug), [quizSlug])

  const isUnknownQuiz = Boolean(quizSlug && !marketingQuizzes[quizSlug])

  const [initialQuizState] = useState(() => restoreQuizState(quiz))

  const [step, setStep] = useState(initialQuizState.step)

  const [answers, setAnswers] = useState<MarketingQuizAnswers>(initialQuizState.answers)

  const [contact, setContact] = useState<Contact>({ name: "", email: "" })

  const [feedback, setFeedback] = useState("")

  const [isSubmitting, setIsSubmitting] = useState(false)

  const [isSuccess, setIsSuccess] = useState(false)

  const [isCalculating, setIsCalculating] = useState(false)

  const [quizResult, setQuizResult] = useState<MarketingQuizResult>(() => evaluateMarketingQuiz(quiz, answers))

  const isResult = step === quiz.questions.length

  const currentQuestion = quiz.questions[step]

  const selected = currentQuestion ? (answers[currentQuestion.id] ?? []) : []

  const progress = Math.round(((step + (isResult ? 1 : 0)) / (quiz.questions.length + 1)) * 100)

  const trafficSource = useMemo(() => {
    const params = new URLSearchParams(window.location.search)
    const utmSource = params.get("utm_source")
    const utmCampaign = params.get("utm_campaign")
    const base = t(quiz.trafficSourceKey)
    const parts = [base, utmSource && `src:${utmSource}`, utmCampaign && `camp:${utmCampaign}`].filter(Boolean)

    return parts.join(" | ").slice(0, 80)
  }, [quiz.trafficSourceKey, t])

  useEffect(() => {
    if (!isResult || isSuccess) return

    setIsCalculating(true)

    const timeout = window.setTimeout(() => {
      setQuizResult(evaluateMarketingQuiz(quiz, answers))
      setIsCalculating(false)
    }, calculationDelayMs)

    return () => window.clearTimeout(timeout)
  }, [answers, isResult, isSuccess, quiz])

  if (isUnknownQuiz) return <Navigate to={getQuizPath(defaultMarketingQuizSlug)} replace />

  const getQuestionText = (question: MarketingQuizQuestion): QuestionText =>
    t(`quizzes.${quiz.slug}.questions.${question.id}`, { returnObjects: true }) as QuestionText

  const getOptionText = (question: MarketingQuizQuestion, optionId: string) =>
    getQuestionText(question).options[optionId] ?? optionId

  const selectOption = (optionId: string) => {
    if (!currentQuestion) return

    setFeedback("")

    const values = answers[currentQuestion.id] ?? []
    const nextAnswers = {
      ...answers,
      [currentQuestion.id]: currentQuestion.multiple
        ? values.includes(optionId)
          ? values.filter((value) => value !== optionId)
          : [...values, optionId]
        : [optionId],
    }
    const nextStep = currentQuestion.multiple ? step : step + 1

    setAnswers(nextAnswers)
    setStep(nextStep)
    persistQuizState(quiz, nextAnswers, nextStep)
  }

  const continueQuiz = () => {
    if (!currentQuestion) return
    if (selected.length === 0) return setFeedback(t("errors.select_option"))

    trackMarketingEvent("lead_quiz_step_complete", { question: currentQuestion.id, quiz: quiz.slug, step: step + 1 })

    setFeedback("")
    setStep(step + 1)
    persistQuizState(quiz, answers, step + 1)
  }

  const goBack = () => {
    const previousStep = Math.max(step - 1, 0)

    setFeedback("")

    setIsCalculating(false)

    setStep(previousStep)

    persistQuizState(quiz, answers, previousStep)
  }

  const submit = async (event: FormEvent) => {
    event.preventDefault()

    setIsSubmitting(true)
    setFeedback("")

    try {
      await handleCreateLandingLead({
        name: contact.name,
        email: contact.email,
        trafficSource,
        formName: t(quiz.formNameKey),
        answers: [
          { label: t("admin_answers.quiz"), value: quiz.slug },
          { label: t("admin_answers.outcome"), value: quizResult.outcomeId },
          { label: t("admin_answers.tags"), value: quizResult.tags.join(", ") },
          ...quiz.questions.map((question) => ({
            label: getQuestionText(question).title,
            value: (answers[question.id] ?? []).map((optionId) => getOptionText(question, optionId)).join(", "),
          })),
        ],
      })

      setIsSuccess(true)

      trackMarketingEvent("lead_quiz_submit_success", {
        source: trafficSource,
        quiz: quiz.slug,
        outcome: quizResult.outcomeId,
      })

      trackLeadConversion({
        content_name: quiz.slug,
        status: "lead_created",
        traffic_source: trafficSource,
        outcome: quizResult.outcomeId,
      })
    } catch {
      setFeedback(t("errors.submit"))
    } finally {
      setIsSubmitting(false)
    }
  }

  const openSignUp = () => {
    const params = new URLSearchParams()

    params.set("quiz", quiz.slug)
    params.set("quizOutcome", quizResult.outcomeId)
    params.set("email", contact.email.trim())
    params.set("name", contact.name.trim())

    navigate(`${PATH_MAP.ROOT_SIGN_UP}?${params.toString()}`)
  }

  return (
    <div className={`${publicAnimatedBackgroundShellClassName} min-h-dvh overflow-x-hidden bg-white text-slate-950`}>
      <PublicAnimatedBackground baseColor="#ffffff" />

      <SEO
        title={t(`quizzes.${quiz.slug}.seo_title`)}
        description={t(`quizzes.${quiz.slug}.seo_description`)}
        path={getQuizPath(quiz.slug)}
      />

      <Navbar showReturn={true} showFunctions={false} />

      <main className="relative mx-auto flex min-h-dvh w-full items-start justify-center overflow-hidden px-3 pb-6 pt-28 sm:px-6 sm:pb-10">
        <div className="absolute left-[-8rem] top-24 h-80 w-80 rounded-full bg-orange-300/30 blur-3xl" />
        <div className="absolute bottom-0 right-[-8rem] h-80 w-80 rounded-full bg-red-300/25 blur-3xl" />
        <div className="absolute left-1/2 top-1/2 h-96 w-96 -translate-x-1/2 -translate-y-1/2 rounded-full bg-amber-100/50 blur-3xl" />

        <div className="relative w-full max-w-3xl">
          <Animate key={`${step}-${isSuccess}-${isCalculating}`} type={AnimationTypes.zoomIn} duration="0.45s">
            <section className="mx-auto w-full rounded-lg border-2 border-orange-200 bg-white/95 p-4 shadow-2xl shadow-orange-300/30 backdrop-blur sm:p-7">
              <div className="mb-6 rounded-lg border-2 border-orange-100 bg-gradient-to-r from-orange-50 via-white to-red-50 p-3 sm:p-4">
                <div className="flex items-center justify-between gap-3 text-xs font-black uppercase tracking-wide text-slate-500">
                  <span>{t("progress", { current: step + 1, total: quiz.questions.length + 1 })}</span>
                  <span className="rounded-full bg-white px-2.5 py-1 text-orange-600 shadow-sm">{progress}%</span>
                </div>

                <div className="mt-3 h-2.5 overflow-hidden rounded-full border border-orange-100 bg-white shadow-inner">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-red-600 via-orange-500 to-amber-400 transition-all duration-500"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>

              {isSuccess ? (
                <Success t={t} quiz={quiz} outcomeId={quizResult.outcomeId} onClick={openSignUp} />
              ) : isCalculating ? (
                <Calculating t={t} />
              ) : isResult ? (
                <ResultForm
                  t={t}
                  quiz={quiz}
                  outcomeId={quizResult.outcomeId}
                  contact={contact}
                  setContact={setContact}
                  feedback={feedback}
                  isSubmitting={isSubmitting}
                  onSubmit={submit}
                  onBack={goBack}
                />
              ) : (
                currentQuestion && (
                  <QuestionStep
                    t={t}
                    quiz={quiz}
                    question={currentQuestion}
                    step={step}
                    selected={selected}
                    feedback={feedback}
                    onSelect={selectOption}
                    onContinue={continueQuiz}
                    onBack={goBack}
                  />
                )
              )}
            </section>
          </Animate>
        </div>
      </main>
    </div>
  )
}

const QuestionStep = ({
  t,
  quiz,
  question,
  step,
  selected,
  feedback,
  onSelect,
  onContinue,
  onBack,
}: {
  t: TFunction<"leadQuiz">
  quiz: MarketingQuiz
  question: MarketingQuizQuestion
  step: number
  selected: string[]
  feedback: string
  onSelect: (optionId: string) => void
  onContinue: () => void
  onBack: () => void
}) => {
  const questionText = t(`quizzes.${quiz.slug}.questions.${question.id}`, { returnObjects: true }) as QuestionText

  return (
    <>
      <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
        <div className="text-center">
          <h2 className="text-2xl font-black leading-tight sm:text-3xl">{questionText.title}</h2>
          <p className="mx-auto mt-3 max-w-2xl text-justify text-sm leading-relaxed text-slate-600 sm:text-base">
            {questionText.subtitle}
          </p>

          {question.multiple && (
            <p className="mt-3 rounded-lg border border-orange-200 bg-orange-50 px-3 py-2 text-center text-xs font-bold text-orange-700">
              {t("multiple_hint")}
            </p>
          )}
        </div>
      </Animate>

      <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
        <div className="mt-6 grid gap-3 sm:grid-cols-2">
          {question.options.map((option, index) => {
            const Icon = optionIcons[(step * 3 + index) % optionIcons.length]
            const active = selected.includes(option.id)

            return (
              <Animate key={option.id} type={AnimationTypes.fadeInTraslate} duration={1.2 + `${index * 0.4}s`}>
                <button
                  type="button"
                  onClick={() => onSelect(option.id)}
                  className={`group flex min-h-18 cursor-pointer items-center gap-3 rounded-lg border-2 p-3 text-left transition-all duration-300 hover:-translate-y-0.5 active:scale-[0.98] ${active ? "border-orange-500 bg-gradient-to-r from-orange-50 to-red-50 shadow-lg shadow-orange-200/60" : "border-slate-200 bg-white shadow-sm hover:border-orange-300 hover:shadow-md"}`}
                  data-interaction-description-key="interactionDescriptions:pages.landPage.leadQuizLandingPage.button.selectOptionOption"
                >
                  <span
                    className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br text-white shadow-md transition-all duration-300 group-hover:rotate-3 group-hover:scale-110 ${active ? "from-red-600 to-orange-500 shadow-orange-300/60" : "from-orange-500 to-red-500 shadow-red-200/50 group-hover:from-red-600 group-hover:to-orange-400"}`}
                  >
                    <Icon size={21} />
                  </span>

                  <span className="flex-1 text-sm font-extrabold text-slate-800">
                    {questionText.options[option.id]}
                  </span>

                  {active && (
                    <span className="flex h-7 w-7 items-center justify-center rounded-full bg-orange-500 text-white shadow-sm">
                      <Check size={16} />
                    </span>
                  )}
                </button>
              </Animate>
            )
          })}
        </div>
      </Animate>

      <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
        {feedback && <p className="mt-4 text-center text-sm font-bold text-red-600">{feedback}</p>}
      </Animate>

      <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
        <div className="mt-6 flex gap-2">
          {step > 0 && (
            <button
              type="button"
              onClick={onBack}
              className="inline-flex min-h-12 cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-indigo-300 bg-gradient-to-r from-indigo-600 to-sky-500 px-4 text-sm font-black text-white shadow-lg shadow-indigo-300/30 transition-all duration-300 hover:-translate-y-0.5 hover:from-indigo-700 hover:to-sky-600 active:scale-[0.98]"
              data-interaction-description-key="interactionDescriptions:pages.landPage.leadQuizLandingPage.button.back"
            >
              <ArrowLeft size={17} /> {t("back")}
            </button>
          )}

          {selected.length > 0 && (
            <button
              type="button"
              onClick={onContinue}
              className="inline-flex min-h-12 flex-1 cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-orange-400 bg-gradient-to-r from-red-600 to-orange-500 px-5 text-sm font-black text-white shadow-lg shadow-red-500/20 transition-all duration-300 hover:-translate-y-0.5 active:scale-[0.98]"
              data-interaction-description-key="interactionDescriptions:pages.landPage.leadQuizLandingPage.button.continueQuiz"
            >
              {step === quiz.questions.length - 1 ? t("see_result") : t("continue")} <ArrowRight size={17} />
            </button>
          )}
        </div>
      </Animate>
    </>
  )
}

const Calculating = ({ t }: CalculatingProps) => (
  <div className="py-10 text-center">
    <Animate type={AnimationTypes.fadeInTraslate} duration="1s">
      <span className="mx-auto flex h-20 w-20 items-center justify-center rounded-lg bg-gradient-to-br from-red-600 to-orange-500 text-white shadow-xl shadow-orange-300/40">
        <LoaderCircle className="animate-spin" size={46} />
      </span>
    </Animate>

    <h2 className="mt-6 text-3xl font-black leading-tight">{t("calculating.title")}</h2>
    <p className="mx-auto mt-3 max-w-lg text-sm leading-relaxed text-slate-600">{t("calculating.description")}</p>
  </div>
)

const ResultForm = ({
  t,
  quiz,
  outcomeId,
  contact,
  setContact,
  feedback,
  isSubmitting,
  onSubmit,
  onBack,
}: ResultFormProps) => (
  <form onSubmit={onSubmit}>
    <Animate type={AnimationTypes.fadeInTraslate} duration="1.2s">
      <div className="text-center">
        <h2 className="text-2xl font-black leading-tight sm:text-3xl">
          {t(`quizzes.${quiz.slug}.outcomes.${outcomeId}.title`)}
        </h2>
        <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-slate-600">
          {t(`quizzes.${quiz.slug}.outcomes.${outcomeId}.description`)}
        </p>
      </div>
    </Animate>

    <div className="mt-6 flex flex-col gap-4">
      <InputField
        type="text"
        icon={faUser}
        required
        value={contact.name}
        onChange={(event) => setContact({ ...contact, name: event.target.value })}
        placeholder={t("result.fields.name")}
        onInvalidMessage={t("lead_form.invalid_required", { ns: "landpage" })}
        descriptionKey="interactionDescriptions:pages.landPage.leadQuizLandingPage.input.name"
      />

      <InputField
        type="email"
        icon={faAt}
        required
        value={contact.email}
        onChange={(event) => setContact({ ...contact, email: event.target.value })}
        placeholder={t("result.fields.email")}
        onInvalidMessage={t("lead_form.invalid_email", { ns: "landpage" })}
        descriptionKey="interactionDescriptions:pages.landPage.leadQuizLandingPage.input.email"
      />
    </div>

    {feedback && <p className="mt-3 text-center text-sm font-bold text-red-600">{feedback}</p>}

    <Animate type={AnimationTypes.fadeInTraslate} duration="1.2s">
      <div className="mt-4 flex gap-2">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex min-h-13 cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-indigo-300 bg-gradient-to-r from-indigo-600 to-sky-500 px-4 text-sm font-black text-white shadow-lg shadow-indigo-300/30 transition-all duration-300 hover:-translate-y-0.5 hover:from-indigo-700 hover:to-sky-600 active:scale-[0.98]"
          data-interaction-description-key="interactionDescriptions:pages.landPage.leadQuizLandingPage.button.onBack"
        >
          <ArrowLeft size={17} /> {t("back")}
        </button>

        <button
          disabled={isSubmitting}
          type="submit"
          className="inline-flex min-h-12 flex-1 cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-orange-400 bg-gradient-to-r from-red-600 to-orange-500 px-5 text-sm font-black text-white shadow-lg shadow-red-500/20 transition-all duration-300 hover:-translate-y-0.5 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-70"
          data-interaction-description-key="interactionDescriptions:pages.landPage.leadQuizLandingPage.button.submitting"
        >
          <Sparkles size={18} />
          {isSubmitting ? t("result.submitting") : t(`quizzes.${quiz.slug}.outcomes.${outcomeId}.primary_cta`)}
        </button>
      </div>
    </Animate>
  </form>
)

const Success = ({ t, quiz, outcomeId, onClick }: SuccessProps) => (
  <div className="py-8 text-center">
    <Animate type={AnimationTypes.fadeInTraslate} duration="1.2s">
      <span className="mx-auto flex h-20 w-20 items-center justify-center rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-xl shadow-emerald-300/40">
        <CheckCircle2 size={48} />
      </span>
    </Animate>

    <Animate type={AnimationTypes.fadeInTraslate} duration="1.6s">
      <h2 className="mt-5 text-3xl font-black">{t("result.success_title")}</h2>
    </Animate>

    <Animate type={AnimationTypes.fadeInTraslate} duration="1.8s">
      <p className="mx-auto mt-3 max-w-lg text-justify text-sm leading-relaxed text-slate-600">
        {t(`quizzes.${quiz.slug}.outcomes.${outcomeId}.success_description`)}
      </p>
    </Animate>

    <Animate type={AnimationTypes.fadeInTraslate} duration="2s">
      <button
        type="button"
        onClick={onClick}
        className="mt-7 inline-flex min-h-12 cursor-pointer items-center gap-2 rounded-lg border-2 border-orange-400 bg-gradient-to-r from-red-600 to-orange-500 px-6 text-sm font-black text-white shadow-lg shadow-red-500/20 transition-all duration-300 hover:-translate-y-0.5 active:scale-[0.98]"
        data-interaction-description-key="interactionDescriptions:pages.landPage.leadQuizLandingPage.button.onClick"
      >
        <ArrowRight size={17} /> {t(`quizzes.${quiz.slug}.outcomes.${outcomeId}.success_cta`)}
      </button>
    </Animate>
  </div>
)
